import { formatISO, parseISO, subDays } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { AccountingMigrationResponse } from "@common/schemas/accounting-integration.schema";
import { Entity } from "@common/types/entity";
import { formatDateOnly } from "@helpers/date";

export type AccountingMigrationStatus = "complete" | "in_progress";

export interface AccountingMigrationRow {
    id: number;
    entity: Entity;
    accountingMigration: AccountingMigrationResponse;
    status: AccountingMigrationStatus;
}

export function formatMigrationStartDate(
    startDate: string | null | undefined,
): string {
    if (!startDate) {
        return "All history";
    }
    return formatDateOnly(startDate, fullDateLongFormat);
}

export function getMigrationEndDate(
    bookkeepingStartDate: string | null | undefined,
): string | undefined {
    if (!bookkeepingStartDate) {
        return undefined;
    }
    try {
        return formatISO(subDays(parseISO(bookkeepingStartDate), 1), {
            representation: "date",
        });
    } catch {
        return undefined;
    }
}

export function formatMigrationEndDate(
    bookkeepingStartDate: string | null | undefined,
): string {
    return formatDateOnly(
        getMigrationEndDate(bookkeepingStartDate),
        fullDateLongFormat,
    );
}

export function buildAccountingMigrationRows(
    entities: Entity[],
    accountingMigrations: AccountingMigrationResponse[],
): AccountingMigrationRow[] {
    const accountingMigrationsByEntityId = new Map(
        accountingMigrations.map((accountingMigration) => [
            accountingMigration.entityId,
            accountingMigration,
        ]),
    );

    return entities.flatMap((entity) => {
        const accountingMigration = accountingMigrationsByEntityId.get(
            entity.id,
        );
        if (!accountingMigration) {
            return [];
        }

        const isComplete =
            accountingMigration.accountsMappingFinalizedAt != null &&
            accountingMigration.journalEntriesSyncedAt != null;
        const status = isComplete ? "complete" : "in_progress";

        return [
            {
                id: entity.id,
                entity,
                accountingMigration,
                status,
            },
        ];
    });
}
